import type { LayerSpec } from '@/core/pipeline';
import { fbm2 } from '@/lib/noise';
import { lerpRGB, parseHex, type RGB } from '@/lib/color';

type PerlinParams = {
  scale: number;       // wavelength of base octave in px
  octaves: number;
  persistence: number; // amplitude falloff per octave
  contrast: number;
  a: string;
  b: string;
};

export const perlinSpec: LayerSpec = {
  kind: 'perlin',
  category: 'generator',
  animated: false,
  label: 'Perlin Noise',
  defaults: {
    scale: 64, octaves: 4, persistence: 0.5, contrast: 1,
    a: '#000000', b: '#ffffff',
  } satisfies PerlinParams,
  schema: {
    scale:       { type: 'number', label: 'Scale',       min: 4,   max: 512, step: 1,    default: 64 },
    octaves:     { type: 'int',    label: 'Octaves',     min: 1,   max: 8,   step: 1,    default: 4 },
    persistence: { type: 'number', label: 'Persistence', min: 0,   max: 1,   step: 0.01, default: 0.5 },
    contrast:    { type: 'number', label: 'Contrast',    min: 0.1, max: 4,   step: 0.05, default: 1 },
    a:           { type: 'color',  label: 'Low',         default: '#000000' },
    b:           { type: 'color',  label: 'High',        default: '#ffffff' },
  },
  run: (_input, params, ctx) => {
    const p = params as PerlinParams;
    const colA = parseHex(p.a) ?? ([0, 0, 0] as RGB);
    const colB = parseHex(p.b) ?? ([255, 255, 255] as RGB);
    const w = ctx.width, h = ctx.height;
    const out = new ImageData(w, h);
    const data = out.data;
    const scale = Math.max(1, p.scale);
    const oct = Math.max(1, Math.min(8, Math.floor(p.octaves)));
    const pers = clamp01(p.persistence);
    const contrast = Math.max(0.001, p.contrast);

    let i = 0;
    for (let y = 0; y < h; y++) {
      for (let x = 0; x < w; x++, i += 4) {
        const n = fbm2(ctx.seed, x / scale, y / scale, oct, pers);
        // remap [-1,1] → [0,1]
        let t = (n + 1) * 0.5;
        // contrast: pull toward midpoint or push away
        t = contrastCurve(t, contrast);
        const c = lerpRGB(colA, colB, t);
        data[i] = c[0]; data[i+1] = c[1]; data[i+2] = c[2]; data[i+3] = 255;
      }
    }
    return out;
  },
};

function clamp01(n: number): number {
  return n < 0 ? 0 : n > 1 ? 1 : n;
}

function contrastCurve(t: number, c: number): number {
  // c=1: identity. c<1: flatten. c>1: steepen around 0.5 via S-curve.
  if (c === 1) return t;
  const x = (t - 0.5) * 2; // [-1, 1]
  const y = Math.sign(x) * Math.pow(Math.abs(x), 1 / c);
  return y * 0.5 + 0.5;
}
